import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, formatDate, formatDateTime, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, MapPin, Calendar, ClipboardList, FileText, Share2, Baby,
  Plus, AlertTriangle, Pencil, Eye, CheckCircle2, HandHeart, CalendarClock, ListChecks,
  Activity, ShieldCheck, X, Target, Trash2, FileSignature, Download, BookOpenCheck, Copy, AlertOctagon,
  Users2, UserPlus, Search,
} from "lucide-react";
import { CareSeekerForm } from "@/pages/CareSeekers";
import IntakeEditDialog from "@/components/IntakeEditDialog";
import MetadataFooter from "@/components/MetadataFooter";
import AttachmentsPanel from "@/components/AttachmentsPanel";

export default function CareSeekerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const [cs, setCS] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [notes, setNotes] = useState([]);
  const [maternal, setMaternal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showAccessLog, setShowAccessLog] = useState(false);
  const [showGenerateDoc, setShowGenerateDoc] = useState(false);
  const [portalInvite, setPortalInvite] = useState(null);  // { url, email, email_sent }
  const [inviting, setInviting] = useState(false);
  const [showSafetyFlag, setShowSafetyFlag] = useState(false);
  const [showLogContact, setShowLogContact] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddAppt, setShowAddAppt] = useState(false);
  const [editingIntakeId, setEditingIntakeId] = useState(null);
  const [tab, setTab] = useState("timeline");
  const [users, setUsers] = useState([]);              // navigator picker
  const [savingAssignee, setSavingAssignee] = useState(false);

  const canAssign = !!can("care_seekers.edit");

  useEffect(() => {
    if (!canAssign) return;
    api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => {});
  }, [canAssign]);

  const updateAssignee = async (assigned_to) => {
    setSavingAssignee(true);
    try {
      const { data } = await api.patch(`/care-seekers/${id}`, {
        assigned_to: assigned_to || null,
      });
      setCS(data);
      toast.success(assigned_to ? "Navigator assigned" : "Navigator cleared");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingAssignee(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [csRes, tlRes, intakeRes, tasksRes, apptRes, refRes, notesRes, matRes] = await Promise.all([
        api.get(`/care-seekers/${id}`),
        api.get(`/care-seekers/${id}/timeline`),
        api.get(`/intakes?care_seeker_id=${id}`),
        api.get(`/tasks?care_seeker_id=${id}`),
        api.get(`/appointments?care_seeker_id=${id}`),
        api.get(`/referrals?care_seeker_id=${id}`),
        api.get(`/notes?care_seeker_id=${id}`),
        api.get(`/maternal?care_seeker_id=${id}`),
      ]);
      setCS(csRes.data);
      setTimeline(tlRes.data);
      setIntakes(intakeRes.data);
      setTasks(tasksRes.data || []);
      setAppointments(apptRes.data || []);
      setReferrals(refRes.data || []);
      setNotes(notesRes.data || []);
      setMaternal(matRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>;
  if (!cs) return <div className="text-center py-12 text-[#1E3A8A]">Care Seeker not found.</div>;

  const iconForType = (t) => {
    if (t === "intake") return <ClipboardList className="w-4 h-4" />;
    if (t === "note") return <FileText className="w-4 h-4" />;
    if (t === "referral") return <Share2 className="w-4 h-4" />;
    if (t === "maternal") return <Baby className="w-4 h-4" />;
    if (t === "outreach") return <HandHeart className="w-4 h-4" />;
    if (t === "task") return <ListChecks className="w-4 h-4" />;
    if (t === "appointment") return <CalendarClock className="w-4 h-4" />;
    if (t === "contact") return <Phone className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  // Batch 7 (Updates doc): "Remove Outreach from the Care Seeker Profile" and
  // bump Referrals + Intakes to be more visible (right after the Timeline).
  // Maternal & Family is part of the Care Seeker profile, with follow-up edits
  // available inside the MaternalSection tab.
  const TABS = [
    { key: "timeline", label: "Timeline", icon: Activity, count: timeline.length },
    { key: "referrals", label: "Referrals", icon: Share2, count: referrals.length },
    { key: "intakes", label: "Intakes", icon: ClipboardList, count: intakes.length },
    { key: "care_plan", label: "Wellness Roadmap", icon: Target, count: null },
    { key: "notes", label: "Follow-Up Notes", icon: FileText, count: notes.length },
    { key: "appointments", label: "Appointments", icon: CalendarClock, count: appointments.length },
    { key: "tasks", label: "Tasks", icon: ListChecks, count: tasks.length },
    { key: "maternal", label: "Maternal & Family", icon: Baby, count: maternal.length },
    { key: "documents", label: "Documents", icon: FileText, count: null },
  ];

  return (
    <div className="space-y-6" data-testid="care-seeker-detail">
      <Link to="/care-seekers" className="inline-flex items-center gap-2 text-sm text-[#1E3A8A] hover:text-[#1E3A8A]" data-testid="back-to-list">
        <ArrowLeft className="w-4 h-4" /> Back to Care Seekers
      </Link>

      {/* Hero */}
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="hw-eyebrow">Care Seeker</div>
              {cs.display_id && (
                <button
                  type="button"
                  onClick={() => {
                    const url = `${window.location.origin}/cs/${cs.display_id}`;
                    navigator.clipboard?.writeText(url);
                    toast.success(`Link copied — paste it in Microsoft Teams or email to share this profile`);
                  }}
                  data-testid="cs-display-id"
                  title="Click to copy a shareable link"
                  className="font-mono text-[10px] tracking-wider text-[#1E3A8A] uppercase bg-[#E0E7FF] hover:bg-[#C7D2FE] px-2 py-0.5 rounded-full transition-colors cursor-pointer"
                >
                  {cs.display_id}
                </button>
              )}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
              {cs.preferred_name || cs.first_name} {cs.last_name}
            </h1>
            {cs.preferred_name && (
              <div className="text-sm text-[#1E3A8A] mt-1">Legal name: {cs.first_name} {cs.last_name}</div>
            )}
            {cs.safety_flag?.active && (
              <button
                onClick={() => setShowSafetyFlag(true)}
                data-testid="safety-flag-pill"
                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5] text-xs font-semibold hover:bg-[#FECACA] animate-pulse"
              >
                <AlertOctagon className="w-3.5 h-3.5" />
                Safety follow-up needed
              </button>
            )}
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#334155]">
              {cs.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4 text-[#1E3A8A]" />{cs.phone}</span>}
              {cs.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4 text-[#1E3A8A]" />{cs.email}</span>}
              {cs.county && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#1E3A8A]" />{cs.county} County</span>}
              {cs.dob && <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#1E3A8A]" />{formatDate(cs.dob)}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {can("care_seekers.edit") && (
              <button
                onClick={() => setShowLogContact(true)}
                data-testid="log-contact-btn"
                title="Quickly stamp last contact for this Care Seeker"
                className="px-4 py-2 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] border border-[#1E3A8A]/20 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#1E3A8A]/20"
              >
                <Phone className="w-4 h-4" /> Log Contact
              </button>
            )}
            {can("care_seekers.edit") && (
              <button
                onClick={() => setShowEdit(true)}
                data-testid="edit-care-seeker-btn"
                className="px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#334155] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#F9F8F6]"
              >
                <Pencil className="w-4 h-4" /> Edit profile
              </button>
            )}
            {can("audit.view") && (
              <button
                onClick={() => setShowAccessLog(true)}
                data-testid="open-access-log-btn"
                title="See everyone who has viewed this record"
                className="px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#334155] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#F9F8F6]"
              >
                <ShieldCheck className="w-4 h-4" /> Access log
              </button>
            )}
            <Link to={`/intake?care_seeker_id=${cs.id}`} className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium inline-flex items-center gap-2" data-testid="start-intake-btn">
              <ClipboardList className="w-4 h-4" /> {intakes.length > 0 ? "Start another intake" : "Start Intake"}
            </Link>
            <button onClick={() => setShowAddNote(true)} className="px-4 py-2 rounded-full bg-[#E8ECE7] text-[#1E3A8A] text-sm font-medium inline-flex items-center gap-2" data-testid="add-note-btn">
              <Plus className="w-4 h-4" /> Note
            </button>
            {can("appointments.create") && (
              <button onClick={() => setShowAddAppt(true)} className="px-4 py-2 rounded-full bg-[#E8ECE7] text-[#1E3A8A] text-sm font-medium inline-flex items-center gap-2" data-testid="schedule-appt-btn">
                <CalendarClock className="w-4 h-4" /> Schedule
              </button>
            )}
            <Link to={`/referrals?care_seeker_id=${cs.id}`} className="px-4 py-2 rounded-full border border-[#E5E5E0] text-[#334155] text-sm font-medium inline-flex items-center gap-2" data-testid="add-referral-btn">
              <Share2 className="w-4 h-4" /> Refer
            </Link>
            <button
              type="button"
              onClick={() => setShowGenerateDoc(true)}
              data-testid="generate-document-btn"
              className="px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#334155] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#F9F8F6]"
            >
              <FileSignature className="w-4 h-4" /> Generate doc
            </button>
            <button
              type="button"
              onClick={async () => {
                setInviting(true);
                try {
                  const { data } = await api.post(`/portal/invite/${cs.id}`, {});
                  setPortalInvite(data);
                  toast.success(data.email_sent ? "Portal link emailed" : "Portal link ready to share");
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setInviting(false); }
              }}
              disabled={inviting}
              data-testid="send-portal-access-btn"
              className="px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#334155] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#F9F8F6] disabled:opacity-50"
            >
              <BookOpenCheck className="w-4 h-4" /> {inviting ? "Sending…" : "Send portal access"}
            </button>
            {can("care_seekers.delete") && (
              <button
                type="button"
                onClick={async () => {
                  const ok = window.confirm(
                    `Delete ${cs.first_name} ${cs.last_name} (${cs.display_id})?\n\n` +
                    `This will hide them and ALL related records (notes, referrals, appointments, intakes, etc.) from every view. The data stays in the database for HIPAA recovery — contact tech support to restore.`
                  );
                  if (!ok) return;
                  try {
                    await api.delete(`/care-seekers/${cs.id}`);
                    toast.success("Care Seeker deleted");
                    navigate("/care-seekers");
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                data-testid="delete-care-seeker-btn"
                className="px-4 py-2 rounded-full bg-white border border-red-200 text-red-700 text-sm font-medium inline-flex items-center gap-2 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
        </div>

        {cs.risk_flags && cs.risk_flags.length > 0 && (
          <div className="mt-6 p-4 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#92400E] flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-[#92400E] mb-1">Risk flags</div>
              <div className="flex flex-wrap gap-1">
                {cs.risk_flags.map((f) => (
                  <span key={f} className="text-xs bg-white px-2 py-1 rounded-full text-[#92400E]">{f}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {cs.notes && (
          <div className="mt-6 p-4 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]">
            <div className="hw-eyebrow mb-2">Notes</div>
            <p className="text-sm text-[#334155] whitespace-pre-wrap">{cs.notes}</p>
          </div>
        )}

        <HouseholdChips csId={cs.id} csLabel={`${cs.preferred_name || cs.first_name} ${cs.last_name}`} />

        <MetadataFooter entity={cs} className="mt-6 pt-4 border-t border-[#F0EFEB]" />
      </div>

      {/* Profile + Tabs */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 space-y-3 self-start">
          <h2 className="font-display text-xl font-semibold text-[#0F172A] mb-2">Profile</h2>
          <InfoRow label="ID" value={cs.display_id} mono />
          <InfoRow label="Status" value={cs.status?.replace(/_/g, " ")} />
          {/* Assigned Care Navigator picker */}
          <div className="flex items-baseline gap-3 py-1">
            <div className="text-xs uppercase tracking-wider text-[#7E937D] w-32 shrink-0">
              Assigned navigator
            </div>
            {canAssign ? (
              <select
                value={cs.assigned_to || ""}
                onChange={(e) => updateAssignee(e.target.value)}
                disabled={savingAssignee}
                data-testid="cs-detail-assign-select"
                className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-[#F9F8F6] border border-[#E5E5E0] text-sm text-[#0F172A] disabled:opacity-50"
              >
                <option value="">— Unassigned —</option>
                {users
                  .filter((u) => u.active !== false)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({ROLE_LABELS[u.role] || u.role?.replace(/_/g, " ")})
                    </option>
                  ))}
              </select>
            ) : (
              <div className="text-sm text-[#0F172A]" data-testid="cs-detail-assigned-readonly">
                {users.find((u) => u.id === cs.assigned_to)?.name || (cs.assigned_to ? "Unknown user" : "—")}
              </div>
            )}
          </div>
          <InfoRow label="Intake channel" value={cs.intake_channel?.replace(/_/g, " ")} />
          <InfoRow label="Preferred contact" value={cs.preferred_contact} />
          <InfoRow label="Household size" value={cs.household_size} />
          <InfoRow label="Address" value={cs.address} />
          <InfoRow label="City" value={cs.city} />
          <InfoRow label="ZIP" value={cs.zip_code} />
          <InfoRow label="Referral source" value={cs.referral_source} />
          <InfoRow label="Consent" value={cs.consent_acknowledged ? `Acknowledged ${formatDate(cs.consent_date)}` : "Not recorded"} />
          <InfoRow label="Added" value={formatDateTime(cs.created_at)} />
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
          {/* Tab nav */}
          <div className="border-b border-[#E5E5E0] overflow-x-auto" data-testid="profile-tabs">
            <div className="flex gap-1 px-2 pt-2">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-xs font-medium whitespace-nowrap transition-colors ${
                    tab === t.key ? "bg-[#E8ECE7] text-[#1E3A8A]" : "text-[#1E3A8A] hover:bg-[#F9F8F6]"
                  }`}
                >
                  <t.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                  {t.label}
                  {t.count !== null && t.count > 0 && (
                    <span className="ml-0.5 text-[9px] bg-white border border-[#E5E5E0] px-1.5 rounded-full text-[#334155]">{t.count}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="p-5">
            {tab === "timeline" && <TimelineSection events={timeline} iconForType={iconForType} referrals={referrals} csId={cs.id} onChange={load} />}
            {tab === "care_plan" && <CarePlanSection csId={cs.id} />}
            {tab === "notes" && <NotesSection items={notes} csId={cs.id} onChange={load} />}
            {tab === "appointments" && <AppointmentsSection items={appointments} csId={cs.id} onChange={load} />}
            {tab === "tasks" && <TasksSection items={tasks} csId={cs.id} onChange={load} />}
            {tab === "referrals" && <ReferralsSection items={referrals} csId={cs.id} />}
            {tab === "intakes" && <IntakesSection items={intakes} csId={cs.id} canEdit={!!can("intakes.create")} onEdit={setEditingIntakeId} />}
            {tab === "maternal" && <MaternalSection items={maternal} csId={cs.id} onChange={load} />}
            {tab === "documents" && (
              <AttachmentsPanel parentType="care_seeker" parentId={cs.id} title="Documents" embedded />
            )}
          </div>
        </div>
      </div>

      {showEdit && (
        <CareSeekerForm
          existing={cs}
          onClose={() => setShowEdit(false)}
          onCreated={() => { setShowEdit(false); load(); }}
        />
      )}
      {editingIntakeId && (
        <IntakeEditDialog
          intakeId={editingIntakeId}
          onClose={() => setEditingIntakeId(null)}
          onSaved={() => load()}
        />
      )}
      {showAccessLog && (
        <AccessLogDrawer csId={cs.id} onClose={() => setShowAccessLog(false)} />
      )}
      {showGenerateDoc && (
        <GenerateDocumentDialog cs={cs} onClose={() => setShowGenerateDoc(false)} />
      )}
      {portalInvite && (
        <PortalInviteDialog
          cs={cs}
          invite={portalInvite}
          onClose={() => setPortalInvite(null)}
        />
      )}
      {showSafetyFlag && (
        <SafetyFlagDialog
          cs={cs}
          onClose={() => setShowSafetyFlag(false)}
          onResolved={(updated) => { setShowSafetyFlag(false); load(); }}
        />
      )}
      {showLogContact && (
        <LogContactDialog
          cs={cs}
          onClose={() => setShowLogContact(false)}
          onLogged={() => { setShowLogContact(false); load(); }}
        />
      )}
      {showAddNote && (
        <NoteDialog
          csId={cs.id}
          onClose={() => setShowAddNote(false)}
          onSaved={() => { setShowAddNote(false); load(); }}
        />
      )}
      {showAddAppt && (
        <AppointmentDialog
          csId={cs.id}
          defaultStaffId={user?.id}
          onClose={() => setShowAddAppt(false)}
          onSaved={() => { setShowAddAppt(false); load(); }}
        />
      )}
    </div>
  );
}

function LogContactDialog({ cs, onClose, onLogged }) {
  const [method, setMethod] = useState("phone");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/care-seekers/${cs.id}/log-contact`, {
        method,
        note: note.trim() || null,
      });
      toast.success(`Contact logged · ${cs.preferred_name || cs.first_name}`);
      onLogged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="log-contact-dialog">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-[#E5E5E0]">
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">Log Contact</h3>
          <p className="text-xs text-[#334155] mt-1">
            Stamps &ldquo;last contact&rdquo; for {cs.preferred_name || cs.first_name} {cs.last_name} to right now.
            Use this when you spoke to or texted them but don&apos;t need a full Follow-Up Note.
          </p>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Method</label>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "phone", l: "Phone" },
                { v: "text", l: "Text" },
                { v: "email", l: "Email" },
                { v: "in_person", l: "In person" },
                { v: "other", l: "Other" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setMethod(opt.v)}
                  data-testid={`log-contact-method-${opt.v}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    method === opt.v
                      ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                      : "bg-white text-[#334155] border-[#E5E5E0] hover:bg-[#F9F8F6]"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              data-testid="log-contact-note"
              placeholder="e.g. Left voicemail; will call back tomorrow"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 rounded-b-2xl">
          <button
            onClick={onClose}
            disabled={submitting}
            data-testid="log-contact-cancel"
            className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155]"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="log-contact-confirm"
            className="px-5 py-2 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40"
          >
            {submitting ? "Logging…" : "Log Contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SafetyFlagDialog({ cs, onClose, onResolved }) {
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const flag = cs.safety_flag || {};
  const resolve = async () => {
    if (!notes.trim()) {
      toast.error("Please add a brief note describing how you followed up.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/care-seekers/${cs.id}/safety-flag/clear`, { resolution_notes: notes.trim() });
      toast.success("Safety flag cleared");
      onResolved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="safety-flag-dialog">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#FEE2E2] text-[#991B1B] flex items-center justify-center flex-shrink-0">
            <AlertOctagon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">
              Safety follow-up needed
            </h3>
            <p className="text-xs text-[#334155] mt-1">
              {cs.first_name} submitted a wellness check-in with concerning answers on{" "}
              {flag.triggered_at && new Date(flag.triggered_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}.
            </p>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {(flag.triggers || []).map((t, i) => (
            <div key={i} className="bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-3" data-testid={`safety-flag-trigger-${i}`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#991B1B]">{t.question_id}</div>
              <div className="text-sm text-[#0F172A] mt-1">{t.question_text}</div>
              <div className="text-sm text-[#0F172A] mt-1"><span className="font-semibold">Answer:</span> {t.answer}</div>
            </div>
          ))}
        </div>
        <div className="mt-5">
          <label className="block text-sm font-medium text-[#0F172A] mb-2">How did you follow up?</label>
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Called CS at 3pm — safe at home, scheduled follow-up Friday."
            className="w-full px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm"
            data-testid="safety-flag-notes"
          />
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-full border border-[#E5E5E0] text-sm font-medium text-[#334155] hover:bg-[#F9F8F6]"
            data-testid="safety-flag-cancel"
          >
            Not yet
          </button>
          <button
            onClick={resolve}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-50"
            data-testid="safety-flag-resolve"
          >
            {submitting ? "Saving…" : "Mark resolved"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PortalInviteDialog({ cs, invite, onClose }) {
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually.");
    }
  };
  const expires = invite.expires_at
    ? new Date(invite.expires_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : "30 days";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="portal-invite-dialog">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-2xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
            <BookOpenCheck className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">
              Portal access for {cs.first_name}
            </h3>
            <p className="text-xs text-[#334155] mt-1">
              {invite.email_sent
                ? <>Email sent to <span className="font-medium">{invite.email}</span>. You can also share the link directly below.</>
                : invite.email
                  ? <>Couldn't send to <span className="font-medium">{invite.email}</span> — copy the link and share it by text.</>
                  : "No email on file — copy the link and text it manually."}
            </p>
          </div>
        </div>
        <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-3 mt-3">
          <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A] font-medium mb-1">Sign-in link</div>
          <div className="text-xs text-[#0F172A] break-all font-mono" data-testid="portal-invite-url">{invite.url}</div>
        </div>
        <p className="text-xs text-[#7E937D] mt-2">Link works until {expires}. Sending another link invalidates this one.</p>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-full border border-[#E5E5E0] text-sm font-medium text-[#334155] hover:bg-[#F9F8F6]" data-testid="portal-invite-close">
            Done
          </button>
          <button onClick={copyLink} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]" data-testid="portal-invite-copy">
            <Copy className="w-4 h-4" /> {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
      </div>
    </div>
  );
}

function GenerateDocumentDialog({ cs, onClose }) {
  const [templates, setTemplates] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState(null); // template doc to email

  useEffect(() => {
    api
      .get("/document-templates")
      .then(({ data }) => setTemplates(data || []))
      .catch((e) => toast.error(formatApiError(e)));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const generate = async (tpl) => {
    setGeneratingId(tpl.id);
    try {
      const res = await api.post(
        `/care-seekers/${cs.id}/render-document/${tpl.id}`,
        {},
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (tpl.name || "document").replace(/\s+/g, "-");
      a.download = `${cs.display_id || cs.id}-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Generated "${tpl.name}"`);
      onClose();
    } catch (e) {
      // Blob responseType means error data is also a Blob — read it as text
      let msg = e?.message || "Failed to generate document";
      try {
        const txt = await e?.response?.data?.text?.();
        if (txt) {
          try { msg = JSON.parse(txt).detail || msg; } catch { msg = txt; }
        }
      } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40"
      onClick={onClose}
      data-testid="generate-doc-dialog"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">Mailmerge</div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">
              Generate document for {cs.preferred_name || cs.first_name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="generate-doc-close"
            className="p-1.5 rounded-full hover:bg-[#F9F8F6]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          {templates === null ? (
            <div className="text-center py-10 text-[#1E3A8A] text-sm">Loading templates…</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-10" data-testid="generate-doc-empty">
              <FileSignature className="w-8 h-8 text-[#1E3A8A] mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#334155]">
                No active document templates yet.
              </p>
              <Link
                to="/document-templates"
                onClick={onClose}
                className="inline-block mt-3 text-sm text-[#1E3A8A] underline"
              >
                Manage templates
              </Link>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="generate-doc-list">
              {templates.map((t) => (
                <li
                  key={t.id}
                  className="flex items-start gap-3 px-3 py-3 rounded-xl border border-[#E5E5E0] hover:border-[#1E3A8A] transition-colors"
                  data-testid={`generate-doc-row-${t.id}`}
                >
                  <div className="w-9 h-9 rounded-xl bg-[#DBEAFE] text-[#1E40AF] flex items-center justify-center flex-shrink-0">
                    <FileSignature className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#0F172A]">{t.name}</div>
                    {t.description && (
                      <div className="text-xs text-[#334155] mt-0.5 line-clamp-2">{t.description}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={generatingId === t.id}
                    onClick={() => generate(t)}
                    data-testid={`generate-doc-pick-${t.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium disabled:opacity-60"
                  >
                    <Download className="w-3.5 h-3.5" />
                    {generatingId === t.id ? "Generating…" : "PDF"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmailTemplate(t)}
                    data-testid={`generate-doc-email-${t.id}`}
                    title={cs.email ? `Email PDF to ${cs.email}` : "Compose email — recipient required"}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E8ECE7] text-[#1E3A8A] text-xs font-medium hover:bg-[#7E937D] hover:text-white"
                  >
                    <Mail className="w-3.5 h-3.5" /> Email
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {emailTemplate && (
        <EmailDocumentDialog
          cs={cs}
          template={emailTemplate}
          onClose={() => setEmailTemplate(null)}
          onSent={() => { setEmailTemplate(null); onClose(); }}
        />
      )}
    </div>
  );
}

function EmailDocumentDialog({ cs, template, onClose, onSent }) {
  const csFirst = cs.preferred_name || cs.first_name || "there";
  const [toEmail, setToEmail] = useState(cs.email || "");
  const [ccText, setCcText] = useState("");
  const [subject, setSubject] = useState(`${template.name} from HarborWell`);
  const [body, setBody] = useState(
    `Hi {{first_name}},\n\nPlease find the attached ${template.name.toLowerCase()} from HarborWell. Reach out if you have any questions.\n\nThanks,\n{{staff_name}}`
  );
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const recipient = (toEmail || "").trim();
    if (!recipient || !recipient.includes("@")) {
      toast.error("A valid recipient email is required");
      return;
    }
    setSubmitting(true);
    try {
      const cc = ccText.split(",").map((s) => s.trim()).filter((s) => s.includes("@"));
      const { data } = await api.post(
        `/care-seekers/${cs.id}/documents/${template.id}/email`,
        { to_email: recipient, subject, body, cc },
      );
      toast.success(`Sent to ${data.recipient}${data.cc?.length ? ` + ${data.cc.length} cc` : ""}`);
      onSent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/40"
      onClick={onClose}
      data-testid="email-doc-dialog"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">Email document</div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">
              {template.name} → {csFirst}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#F9F8F6]" data-testid="email-doc-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">To</label>
            <input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              data-testid="email-doc-to"
              placeholder="care-seeker@example.com"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            />
            {!cs.email && (
              <p className="text-[10px] text-[#92400E] mt-1">
                No email on this Care Seeker&apos;s profile — type one to send.
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">CC (comma-separated, optional)</label>
            <input
              type="text"
              value={ccText}
              onChange={(e) => setCcText(e.target.value)}
              data-testid="email-doc-cc"
              placeholder="caregiver@example.com, partner@example.com"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="email-doc-subject"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">
              Message
              <span className="font-normal text-[10px] text-[#1E3A8A] ml-2">
                Use {"{{first_name}}"}, {"{{full_name}}"}, {"{{staff_name}}"} as merge fields
              </span>
            </label>
            <textarea
              rows={7}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="email-doc-body"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm font-mono"
            />
          </div>
          <div className="px-3 py-2 rounded-lg bg-[#F9F8F6] border border-[#E5E5E0] text-[11px] text-[#334155] inline-flex items-start gap-2">
            <FileSignature className="w-3.5 h-3.5 mt-0.5 text-[#1E3A8A] flex-shrink-0" />
            <span>
              The <strong>{template.name}</strong> PDF (auto-filled with this Care Seeker&apos;s details) will be attached. Reply-to is set to your email so responses come straight to you.
            </span>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 sticky bottom-0">
          <button onClick={onClose} disabled={submitting} data-testid="email-doc-cancel" className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155]">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="email-doc-send" className="px-5 py-2 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40 inline-flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" />
            {submitting ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AccessLogDrawer({ csId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api
      .get(`/care-seekers/${csId}/access-log`, { params: { limit: 200 } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [csId, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[#0F172A]/40" onClick={onClose} data-testid="access-log-drawer">
      <aside
        className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">Compliance</div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">Access log</h2>
            {data && (
              <div className="text-xs text-[#1E3A8A] mt-0.5">
                {data.count} recent event{data.count === 1 ? "" : "s"} for {data.care_seeker.display_id} {data.care_seeker.first_name} {data.care_seeker.last_name}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="access-log-close" className="p-1.5 rounded-full hover:bg-[#F9F8F6]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          {!data ? (
            <div className="text-center py-12 text-[#1E3A8A] text-sm">Loading…</div>
          ) : data.rows.length === 0 ? (
            <div className="text-center py-12 text-[#1E3A8A] text-sm">
              No access events recorded yet.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="access-log-rows">
              {data.rows.map((r) => (
                <li
                  key={r.id}
                  className="flex items-start gap-3 px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]"
                >
                  <div className="w-8 h-8 rounded-full bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                    {(r.user_name || r.user_email || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <span className="font-medium text-[#0F172A] truncate">{r.user_name || r.user_email}</span>
                      {r.role && (
                        <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">
                          {ROLE_LABELS[r.role] || r.role.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#1E3A8A] mt-0.5">
                      {r.action.replace(/_/g, " ")} · {formatDateTime(r.at)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

// ---------- Tab sections ----------
function EmptyHint({ children }) {
  return <div className="text-center py-8 text-sm text-[#1E3A8A]">{children}</div>;
}

function TimelineSection({ events, iconForType, referrals = [], csId, onChange }) {
  if (events.length === 0) return <EmptyHint>No activity yet. Start an A.N.C.H.O.R. intake or add a follow-up note.</EmptyHint>;

  // Find an existing referral for a given resource (matched by Care Seeker + organization name).
  const findReferral = (orgName) =>
    referrals.find(
      (r) => (r.organization || "").trim().toLowerCase() === (orgName || "").trim().toLowerCase(),
    );

  const markCompleted = async (refId) => {
    try {
      await api.patch(`/referrals/${refId}`, { status: "completed" });
      toast.success("Referral marked completed");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createReferral = async (resource) => {
    try {
      await api.post("/referrals", {
        care_seeker_id: csId,
        category: resource.category,
        organization: resource.name,
        contact_info: [resource.phone, resource.website].filter(Boolean).join(" · "),
        referral_date: new Date().toISOString().slice(0, 10),
        status: "pending",
        notes: `Connected via intake on this Care Seeker's profile.`,
      });
      toast.success(`Draft referral created for ${resource.name}`);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <ol className="space-y-4" data-testid="timeline-list">
      {events.map((evt, idx) => (
        <li key={`${evt.type}-${evt.date}-${idx}`} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="w-9 h-9 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
              {iconForType(evt.type)}
            </div>
            {idx !== events.length - 1 && <div className="w-px flex-1 bg-[#E5E5E0] my-1" />}
          </div>
          <div className="flex-1 pb-4">
            <div className="text-sm font-medium text-[#0F172A] capitalize">
              {evt.type === "intake" ? `A.N.C.H.O.R. intake (${evt.data.intake_type})` :
                evt.type === "note" ? `Follow-up note · ${evt.data.interaction_type?.replace(/_/g, " ")}` :
                evt.type === "referral" ? `Referral · ${evt.data.organization} (${evt.data.category?.replace(/_/g, " ")})` :
                evt.type === "maternal" ? `Maternal & Family · ${evt.data.visit_type?.replace(/_/g, " ")}` :
                evt.type === "outreach" ? `Outreach · ${evt.data.title} (${evt.data.activity_type?.replace(/_/g, " ")})` :
                evt.type === "task" ? `Task · ${evt.data.display_id || ""} ${evt.data.title}` :
                evt.type === "appointment" ? `Appointment · ${evt.data.title} (${evt.data.status})` :
                evt.type === "contact" ? `Contact logged · ${(evt.data.method || "other").replace(/_/g, " ")}${evt.data.by_name ? ` by ${evt.data.by_name}` : ""}` :
                evt.type}
            </div>
            <div className="text-xs text-[#1E3A8A] mt-0.5">{formatDateTime(evt.date)}</div>
            {evt.data.notes && (
              <p className="text-sm text-[#334155] mt-2 whitespace-pre-wrap">{evt.data.notes}</p>
            )}
            {evt.type === "note" && evt.data.emotional_wellness_notes && (
              <p className="text-sm text-[#334155] mt-2 whitespace-pre-wrap">{evt.data.emotional_wellness_notes}</p>
            )}
            {evt.type === "contact" && evt.data.note && (
              <p className="text-sm text-[#334155] mt-2 whitespace-pre-wrap">{evt.data.note}</p>
            )}
            {evt.type === "referral" && (
              <div className="text-xs mt-1 inline-block px-2 py-0.5 rounded-full bg-[#E8ECE7] text-[#1E3A8A] capitalize">
                {evt.data.status?.replace(/_/g, " ")}
              </div>
            )}
            {evt.type === "intake" && (evt.data.connected_resources || []).length > 0 && (
              <div className="mt-3" data-testid={`intake-${evt.data.id}-connected`}>
                <div className="hw-eyebrow text-[10px] mb-2">Connected via intake</div>
                <ul className="space-y-1.5">
                  {evt.data.connected_resources.map((res) => {
                    const ref = findReferral(res.name);
                    const completed = ref?.status === "completed";
                    return (
                      <li
                        key={res.id}
                        data-testid={`connected-resource-${res.id}`}
                        className="flex flex-wrap items-center gap-2 text-xs bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl px-3 py-2"
                      >
                        <span className="font-medium text-[#0F172A]">{res.name}</span>
                        <span className="text-[10px] bg-[#E8ECE7] text-[#1E3A8A] px-2 py-0.5 rounded-full capitalize">
                          {(res.category || "").replace(/_/g, " ")}
                        </span>
                        {res.county && (
                          <span className="text-[10px] text-[#1E3A8A]">· {res.county}</span>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                          {!ref && (
                            <button
                              onClick={() => createReferral(res)}
                              data-testid={`connected-create-ref-${res.id}`}
                              className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] font-medium hover:bg-[#E8ECE7] inline-flex items-center gap-1"
                            >
                              <Share2 className="w-3 h-3" strokeWidth={1.75} /> Create referral
                            </button>
                          )}
                          {ref && !completed && (
                            <>
                              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                ref.status === "pending" ? "bg-[#FEF3C7] text-[#92400E]" :
                                ref.status === "in_progress" ? "bg-[#DBEAFE] text-[#1E40AF]" :
                                "bg-[#F1F5F9] text-[#475569]"
                              }`}>
                                {ref.status.replace(/_/g, " ")}
                              </span>
                              <button
                                onClick={() => markCompleted(ref.id)}
                                data-testid={`connected-mark-completed-${res.id}`}
                                className="text-[11px] px-2.5 py-1 rounded-full bg-[#E8ECE7] text-[#166534] font-medium hover:bg-[#D1F2D9] inline-flex items-center gap-1"
                              >
                                <CheckCircle2 className="w-3 h-3" strokeWidth={1.75} /> Mark completed
                              </button>
                            </>
                          )}
                          {ref && completed && (
                            <span
                              data-testid={`connected-completed-${res.id}`}
                              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E8ECE7] text-[#166534] inline-flex items-center gap-1"
                            >
                              <CheckCircle2 className="w-3 h-3" strokeWidth={1.75} /> Completed
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function CarePlanSection({ csId }) {
  const [plan, setPlan] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/care-seekers/${csId}/care-plan`);
      setPlan(data.plan);
      setGoals(data.goals || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [csId]);
  useEffect(() => { load(); }, [load]);

  const startPlan = async () => {
    try {
      const { data } = await api.post(`/care-seekers/${csId}/care-plan`, { title: "Care plan", summary: null });
      setPlan(data);
      toast.success("Care plan created");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeGoal = async (g) => {
    if (!window.confirm(`Remove goal "${g.title}"?`)) return;
    try {
      await api.delete(`/care-plan-goals/${g.id}`);
      setGoals((prev) => prev.filter((x) => x.id !== g.id));
      toast.success("Goal removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateGoal = async (g, patch) => {
    try {
      const { data } = await api.patch(`/care-plan-goals/${g.id}`, patch);
      setGoals((prev) => prev.map((x) => x.id === g.id ? data : x));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="text-center py-12 text-[#1E3A8A] text-sm">Loading…</div>;

  if (!plan) {
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="care-plan-empty">
        <Target className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
        <h3 className="font-display text-lg font-semibold text-[#0F172A]">No Wellness Roadmap yet</h3>
        <p className="text-sm text-[#334155] mt-2 max-w-md mx-auto">
          Start a Wellness Roadmap to organize goals, referrals, community
          resources, milestones, and follow-up activities with the Care Seeker.
        </p>
        <button
          onClick={startPlan}
          data-testid="start-care-plan-btn"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> Start Wellness Roadmap
        </button>
      </div>
    );
  }

  const totalProgress = goals.length
    ? Math.round(goals.reduce((acc, g) => acc + (g.progress || 0), 0) / goals.length)
    : 0;
  const achievedCount = goals.filter((g) => g.status === "achieved").length;

  return (
    <div className="space-y-4" data-testid="care-plan-section">
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="hw-eyebrow mb-1 text-[10px]">Wellness Roadmap</div>
            <h3 className="font-display text-xl font-semibold text-[#0F172A]">{plan.title === "Care plan" ? "Wellness Roadmap" : plan.title}</h3>
            {plan.summary && <p className="text-sm text-[#334155] mt-1 whitespace-pre-wrap">{plan.summary}</p>}
          </div>
          <div className="text-right">
            <div className="text-xs text-[#1E3A8A] mb-1">Overall progress</div>
            <div className="flex items-center gap-2">
              <div className="w-40 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                <div className="h-full bg-[#1E3A8A] transition-all" style={{ width: `${totalProgress}%` }} />
              </div>
              <span className="text-sm font-semibold text-[#0F172A]">{totalProgress}%</span>
            </div>
            <div className="text-[11px] text-[#1E3A8A] mt-1">
              {achievedCount}/{goals.length} goals achieved
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h4 className="font-display text-base font-semibold text-[#0F172A]">SMART goals</h4>
        <button
          onClick={() => setNewOpen(true)}
          data-testid="add-goal-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-xs font-medium hover:bg-[#172554]"
        >
          <Plus className="w-3.5 h-3.5" /> Add goal
        </button>
      </div>

      {goals.length === 0 ? (
        <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-2xl p-8 text-center text-sm text-[#334155]">
          No goals yet. Add the first one to start tracking progress.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="goal-list">
          {goals.map((g) => (
            <GoalRow
              key={g.id}
              goal={g}
              onEdit={() => setEditing(g)}
              onRemove={() => removeGoal(g)}
              onUpdate={(patch) => updateGoal(g, patch)}
            />
          ))}
        </ul>
      )}

      {newOpen && (
        <GoalForm
          planId={plan.id}
          onClose={() => setNewOpen(false)}
          onSaved={(g) => { setGoals((prev) => [...prev, g]); setNewOpen(false); }}
        />
      )}
      {editing && (
        <GoalForm
          planId={plan.id}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={(g) => { setGoals((prev) => prev.map((x) => x.id === g.id ? g : x)); setEditing(null); }}
        />
      )}
    </div>
  );
}

function GoalRow({ goal, onEdit, onRemove, onUpdate }) {
  const statusStyle = ({
    not_started: "bg-[#F1F5F9] text-[#64748B]",
    in_progress: "bg-[#DBEAFE] text-[#1E40AF]",
    achieved: "bg-[#E8ECE7] text-[#166534]",
    paused: "bg-[#FEF3C7] text-[#92400E]",
    cancelled: "bg-[#FEE2E2] text-[#991B1B]",
  })[goal.status] || "bg-[#F1F5F9] text-[#64748B]";

  return (
    <li
      data-testid={`goal-${goal.id}`}
      className={`bg-white border rounded-2xl p-4 ${goal.status === "achieved" ? "border-[#86EFAC] bg-[#F0FDF4]" : "border-[#E5E5E0]"}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h5 className="font-medium text-[#0F172A]">{goal.title}</h5>
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${statusStyle}`}>
              {goal.status.replace(/_/g, " ")}
            </span>
            {goal.target_date && (
              <span className="text-[11px] text-[#1E3A8A] inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Target {formatDate(goal.target_date)}
              </span>
            )}
          </div>
          {goal.description && <p className="text-xs text-[#334155] mt-1 whitespace-pre-wrap">{goal.description}</p>}
          {goal.notes && <p className="text-xs text-[#7E937D] mt-1 whitespace-pre-wrap"><strong>Notes:</strong> {goal.notes}</p>}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <div className="flex-1 min-w-[140px] h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
              <div className="h-full bg-[#1E3A8A]" style={{ width: `${goal.progress || 0}%` }} />
            </div>
            <input
              type="number"
              min={0}
              max={100}
              value={goal.progress || 0}
              data-testid={`goal-progress-${goal.id}`}
              onChange={(e) => {
                const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                onUpdate({ progress: v });
              }}
              className="w-16 px-2 py-1 rounded-lg border border-[#E5E5E0] text-xs text-right"
            />
            <span className="text-xs text-[#1E3A8A]">%</span>
            <select
              value={goal.status}
              data-testid={`goal-status-${goal.id}`}
              onChange={(e) => onUpdate({ status: e.target.value })}
              className="px-2 py-1 rounded-lg border border-[#E5E5E0] text-xs"
            >
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="achieved">Achieved</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} data-testid={`edit-goal-${goal.id}`} className="text-[11px] text-[#1E3A8A] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#F9F8F6]">
            <Pencil className="w-3 h-3" /> Edit
          </button>
          <button onClick={onRemove} data-testid={`delete-goal-${goal.id}`} className="text-[11px] text-[#991B1B] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#FEE2E2]">
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
      </div>
    </li>
  );
}

function GoalForm({ planId, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: existing?.title || "",
    description: existing?.description || "",
    target_date: existing?.target_date || "",
    status: existing?.status || "not_started",
    progress: existing?.progress ?? 0,
    notes: existing?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (existing) {
        const { data } = await api.patch(`/care-plan-goals/${existing.id}`, form);
        onSaved(data);
        toast.success("Goal updated");
      } else {
        const { data } = await api.post(`/care-plans/${planId}/goals`, form);
        onSaved(data);
        toast.success("Goal added");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="goal-form">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h3 className="font-display text-lg font-semibold">{existing ? "Edit" : "Add"} SMART goal</h3>
          <button onClick={onClose} data-testid="goal-form-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1.5">Goal title *</label>
            <input
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              data-testid="goal-title"
              placeholder="e.g., Secure stable housing within 90 days"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="goal-description"
              placeholder="What does success look like? Make it Specific, Measurable, Achievable, Relevant, Time-bound."
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-1.5">Target date</label>
              <input
                type="date"
                value={form.target_date || ""}
                onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                data-testid="goal-target-date"
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-1.5">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              >
                <option value="not_started">Not started</option>
                <option value="in_progress">In progress</option>
                <option value="achieved">Achieved</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1.5">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]"
              placeholder="Optional context for staff."
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-full border border-[#E5E5E0] text-[#334155] font-medium text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="goal-submit" className="flex-1 py-2.5 rounded-full bg-[#1E3A8A] text-white font-medium text-sm disabled:opacity-60">
              {saving ? "Saving…" : existing ? "Save changes" : "Add goal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function NotesSection({ items, csId, onChange }) {
  const { can } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const canCreate = !!can("notes.create");
  const canEdit = !!can("notes.edit") || canCreate;
  return (
    <div className="space-y-3" data-testid="notes-section">
      {canCreate && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-[#1E3A8A]">
            Use Follow-Up Notes to document referral coordination,
            community-resource navigation, partner follow-up, appointment
            logistics, and service-status updates.
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            data-testid="note-add-btn"
            className="px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#172554]"
          >
            <Plus className="w-3.5 h-3.5" /> New note
          </button>
        </div>
      )}
      {items.length === 0 ? (
        <EmptyHint>
          No follow-up notes yet.{canCreate ? " Use the New note button to log one." : ""}
        </EmptyHint>
      ) : (
        <ul className="space-y-2">
          {items.map((n) => (
            <li key={n.id} className="border border-[#E5E5E0] rounded-xl p-3 hover:bg-[#F9F8F6]" data-testid={`note-row-${n.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-medium text-[#0F172A] capitalize">{n.interaction_type?.replace(/_/g, " ")}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#1E3A8A]">{formatDateTime(n.interaction_date || n.created_at)}</span>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setEditing(n)}
                      data-testid={`note-edit-${n.id}`}
                      title="Edit note"
                      className="p-1 rounded-full hover:bg-[#F1F5F9] text-[#1E3A8A]"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              {n.emotional_wellness_notes && <p className="text-sm text-[#334155] mt-2 whitespace-pre-wrap">{n.emotional_wellness_notes}</p>}
              {n.notes && <p className="text-sm text-[#334155] mt-2 whitespace-pre-wrap">{n.notes}</p>}
              <MetadataFooter entity={n} className="mt-2" />
            </li>
          ))}
        </ul>
      )}
      {showAdd && (
        <NoteDialog
          csId={csId}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); onChange && onChange(); }}
        />
      )}
      {editing && (
        <NoteDialog
          csId={csId}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange && onChange(); }}
        />
      )}
    </div>
  );
}

function NoteDialog({ csId, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [interactionType, setInteractionType] = useState(existing?.interaction_type || "phone");
  const [interactionDate, setInteractionDate] = useState(() => {
    const v = existing?.interaction_date || new Date().toISOString();
    return v.slice(0, 16); // local datetime input format
  });
  const [body, setBody] = useState(existing?.emotional_wellness_notes || existing?.notes || "");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!body.trim()) { toast.error("Please add a note"); return; }
    setSubmitting(true);
    try {
      const payload = {
        interaction_type: interactionType,
        interaction_date: interactionDate ? new Date(interactionDate).toISOString() : new Date().toISOString(),
        emotional_wellness_notes: body.trim(),
      };
      if (isEdit) {
        await api.patch(`/notes/${existing.id}`, payload);
        toast.success("Note updated");
      } else {
        await api.post(`/notes`, { ...payload, care_seeker_id: csId });
        toast.success("Note logged");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid={isEdit ? "note-edit-dialog" : "note-add-dialog"}>
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">{isEdit ? "Edit follow-up note" : "New follow-up note"}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F9F8F6]" data-testid="note-dialog-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">Interaction</label>
              <select value={interactionType} onChange={(e) => setInteractionType(e.target.value)} data-testid="note-type" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm">
                <option value="phone">Phone</option>
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="in_person">In person</option>
                <option value="home_visit">Home visit</option>
                <option value="outreach_event">Outreach event</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">When</label>
              <input type="datetime-local" value={interactionDate} onChange={(e) => setInteractionDate(e.target.value)} data-testid="note-date" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Non-clinical emotional-support observations</label>
            <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="note-body" placeholder="What did you discuss? Any follow-ups needed?" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} disabled={submitting} data-testid="note-cancel" className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155]">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="note-confirm" className="px-5 py-2 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40">
            {submitting ? "Saving…" : (isEdit ? "Save changes" : "Log note")}
          </button>
        </div>
      </div>
    </div>
  );
}

function AppointmentsSection({ items, csId, onChange }) {
  const { can, user } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const canCreate = !!can("appointments.create");
  const canEdit = !!can("appointments.edit") || canCreate;
  return (
    <div className="space-y-3" data-testid="appointments-section">
      {canCreate && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-[#1E3A8A]">Schedule visits, calls, and follow-ups.</div>
          <button type="button" onClick={() => setShowAdd(true)} data-testid="appt-add-btn" className="px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#172554]">
            <Plus className="w-3.5 h-3.5" /> Schedule
          </button>
        </div>
      )}
      {items.length === 0 ? (
        <EmptyHint>No appointments scheduled.{canCreate ? " Use the Schedule button above." : ""}</EmptyHint>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li key={a.id} className={`border rounded-xl p-3 ${a.status === "cancelled" ? "border-[#FCA5A5] opacity-75" : "border-[#E5E5E0] hover:bg-[#F9F8F6]"}`} data-testid={`appt-row-${a.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className={`text-sm font-medium text-[#0F172A] ${a.status === "cancelled" ? "line-through" : ""}`}>{a.title}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">{a.status}</span>
                  {canEdit && a.status !== "cancelled" && (
                    <button type="button" onClick={() => setEditing(a)} data-testid={`appt-edit-${a.id}`} title="Edit appointment" className="p-1 rounded-full hover:bg-[#F1F5F9] text-[#1E3A8A]">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="text-xs text-[#1E3A8A] mt-1">{formatDateTime(a.start_at)}</div>
              {a.description && <p className="text-xs text-[#334155] mt-1 whitespace-pre-wrap line-clamp-2">{a.description}</p>}
            </li>
          ))}
        </ul>
      )}
      {showAdd && <AppointmentDialog csId={csId} defaultStaffId={user?.id} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); onChange && onChange(); }} />}
      {editing && <AppointmentDialog csId={csId} existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChange && onChange(); }} />}
    </div>
  );
}

function AppointmentDialog({ csId, existing, defaultStaffId, onClose, onSaved }) {
  const isEdit = !!existing;
  const [title, setTitle] = useState(existing?.title || "");
  const [startAt, setStartAt] = useState(() => {
    const v = existing?.start_at || new Date(Date.now() + 24 * 3600 * 1000).toISOString();
    return v.slice(0, 16);
  });
  const [durationMins, setDurationMins] = useState(() => {
    if (existing?.start_at && existing?.end_at) {
      const diff = (new Date(existing.end_at) - new Date(existing.start_at)) / 60000;
      return Math.max(15, Math.round(diff));
    }
    return 30;
  });
  const [description, setDescription] = useState(existing?.description || "");
  const [physicalAddress, setPhysicalAddress] = useState(existing?.physical_address || "");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!title.trim()) { toast.error("Please add a title"); return; }
    setSubmitting(true);
    try {
      const startISO = new Date(startAt).toISOString();
      const endISO = new Date(new Date(startAt).getTime() + (parseInt(durationMins, 10) || 30) * 60000).toISOString();
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        start_at: startISO,
        end_at: endISO,
        physical_address: physicalAddress.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/appointments/${existing.id}`, payload);
        toast.success("Appointment updated");
      } else {
        await api.post(`/appointments`, { ...payload, care_seeker_id: csId, staff_user_id: defaultStaffId });
        toast.success("Appointment scheduled");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid={isEdit ? "appt-edit-dialog" : "appt-add-dialog"}>
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">{isEdit ? "Edit appointment" : "Schedule appointment"}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F9F8F6]" data-testid="appt-dialog-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="appt-title" placeholder="e.g. Postpartum check-in" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">When</label>
              <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="appt-start" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">Duration (min)</label>
              <input type="number" min="15" step="15" value={durationMins} onChange={(e) => setDurationMins(e.target.value)} data-testid="appt-duration" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Location (optional)</label>
            <input type="text" value={physicalAddress} onChange={(e) => setPhysicalAddress(e.target.value)} data-testid="appt-location" placeholder="e.g. 123 Main St, Greenville NC" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Notes (optional)</label>
            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="appt-description" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} disabled={submitting} data-testid="appt-cancel" className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155]">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="appt-confirm" className="px-5 py-2 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40">
            {submitting ? "Saving…" : (isEdit ? "Save changes" : "Schedule")}
          </button>
        </div>
      </div>
    </div>
  );
}

function TasksSection({ items, csId, onChange }) {
  const { can } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const canCreate = !!can("tasks.create");
  const canEdit = !!can("tasks.edit") || canCreate;
  return (
    <div className="space-y-3" data-testid="tasks-section">
      {canCreate && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-[#1E3A8A]">Track follow-ups, reminders, and to-dos for this Care Seeker.</div>
          <button type="button" onClick={() => setShowAdd(true)} data-testid="task-add-btn" className="px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#172554]">
            <Plus className="w-3.5 h-3.5" /> New task
          </button>
        </div>
      )}
      {items.length === 0 ? (
        <EmptyHint>No tasks linked to this Care Seeker.{canCreate ? " Use the New task button above." : ""}</EmptyHint>
      ) : (
        <ul className="space-y-2">
          {items.map((t) => (
            <li key={t.id} className="border border-[#E5E5E0] rounded-xl p-3 hover:bg-[#F9F8F6]" data-testid={`task-row-${t.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  {t.display_id && <span className="font-mono text-[10px] tracking-wider text-[#1E3A8A] uppercase">{t.display_id}</span>}
                  <span className={`text-sm font-medium ${t.status === "completed" ? "line-through text-[#1E3A8A]" : "text-[#0F172A]"}`}>{t.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">{t.status?.replace(/_/g, " ")}</span>
                  {canEdit && (
                    <button type="button" onClick={() => setEditing(t)} data-testid={`task-edit-${t.id}`} title="Edit task" className="p-1 rounded-full hover:bg-[#F1F5F9] text-[#1E3A8A]">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              {t.due_date && <div className="text-xs text-[#1E3A8A] mt-1">Due {formatDate(t.due_date)}</div>}
            </li>
          ))}
        </ul>
      )}
      {showAdd && <TaskDialog csId={csId} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); onChange && onChange(); }} />}
      {editing && <TaskDialog csId={csId} existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChange && onChange(); }} />}
    </div>
  );
}

function TaskDialog({ csId, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [title, setTitle] = useState(existing?.title || "");
  const [description, setDescription] = useState(existing?.description || "");
  const [dueDate, setDueDate] = useState(existing?.due_date || "");
  const [priority, setPriority] = useState(existing?.priority || "normal");
  const [status, setStatus] = useState(existing?.status || "open");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!title.trim()) { toast.error("Please add a title"); return; }
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        due_date: dueDate || null,
        priority,
      };
      if (isEdit) {
        await api.patch(`/tasks/${existing.id}`, { ...payload, status });
        toast.success("Task updated");
      } else {
        await api.post(`/tasks`, { ...payload, care_seeker_id: csId });
        toast.success("Task created");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid={isEdit ? "task-edit-dialog" : "task-add-dialog"}>
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">{isEdit ? "Edit task" : "New task"}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F9F8F6]" data-testid="task-dialog-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="task-title" placeholder="e.g. Follow up on housing referral" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Details (optional)</label>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="task-description" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">Due date (optional)</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="task-due" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="task-priority" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          {isEdit && (
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="task-status" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm">
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} disabled={submitting} data-testid="task-cancel" className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155]">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="task-confirm" className="px-5 py-2 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40">
            {submitting ? "Saving…" : (isEdit ? "Save changes" : "Create task")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReferralsSection({ items, csId }) {
  if (items.length === 0) return (
    <EmptyHint>
      No referrals yet. <Link to={`/referrals?care_seeker_id=${csId}`} className="text-[#1E3A8A] font-medium hover:underline">Open Referrals</Link>.
    </EmptyHint>
  );
  return (
    <ul className="space-y-2" data-testid="referrals-section">
      {items.map((r) => (
        <li key={r.id} className="border border-[#E5E5E0] rounded-xl p-3 hover:bg-[#F9F8F6]">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium text-[#0F172A]">{r.organization}</span>
            <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">{r.status?.replace(/_/g, " ")}</span>
          </div>
          <div className="text-xs text-[#1E3A8A] mt-1 capitalize">{r.category?.replace(/_/g, " ")} · {formatDate(r.referral_date)}</div>
          {r.notes && <p className="text-xs text-[#334155] mt-1 whitespace-pre-wrap line-clamp-2">{r.notes}</p>}
        </li>
      ))}
    </ul>
  );
}

function IntakesSection({ items, csId, canEdit, onEdit }) {
  if (items.length === 0) return (
    <EmptyHint>
      No A.N.C.H.O.R. intakes yet. <Link to={`/intake?care_seeker_id=${csId}`} className="text-[#1E3A8A] font-medium hover:underline">Start one</Link>.
    </EmptyHint>
  );
  return (
    <ul className="space-y-2" data-testid="intakes-section">
      {items.map((it) => (
        <li key={it.id} className="border border-[#E5E5E0] rounded-xl p-3 flex flex-wrap items-center justify-between gap-2 hover:bg-[#F9F8F6]">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-[#0F172A] capitalize">{it.intake_type || "full"} intake</span>
              {it.maternal_relevant && <span className="text-[10px] bg-[#FCE7F3] text-[#9D174D] px-2 py-0.5 rounded-full">Maternal</span>}
              {it.feels_safe === false && <span className="text-[10px] bg-[#FEE2E2] text-[#991B1B] px-2 py-0.5 rounded-full">Safety concern</span>}
            </div>
            <MetadataFooter entity={it} className="mt-1" />
          </div>
          <button
            onClick={() => onEdit(it.id)}
            data-testid={`view-intake-${it.id}`}
            className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white"
          >
            <Eye className="w-3.5 h-3.5" /> {canEdit ? "View / edit" : "View"}
          </button>
        </li>
      ))}
    </ul>
  );
}

function MaternalSection({ items, csId, onChange }) {
  const { can } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const canCreate = !!can("maternal.create");
  // `maternal.edit` gates per-row updates; `maternal.create` covers adding new visits.
  // Field-specialists/care-navigators get both in the seeded role defaults.
  const canEdit = !!can("maternal.edit") || !!can("maternal.create");
  return (
    <div className="space-y-3" data-testid="maternal-section">
      {canCreate && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-[#1E3A8A]">
            Log a postpartum check-in, home visit, kit distribution, or family follow-up.
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            data-testid="maternal-add-visit-btn"
            className="px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#172554]"
          >
            <Plus className="w-3.5 h-3.5" /> Log visit
          </button>
        </div>
      )}
      {items.length === 0 ? (
        <EmptyHint>
          No maternal & family visits logged yet.
          {canCreate ? " Use the Log visit button above to record one." : ""}
        </EmptyHint>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li key={m.id} className="border border-[#E5E5E0] rounded-xl p-3 hover:bg-[#F9F8F6]" data-testid={`maternal-row-${m.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-medium text-[#0F172A] capitalize">{m.visit_type?.replace(/_/g, " ")}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#1E3A8A]">{formatDate(m.visit_date)}</span>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setEditing(m)}
                      data-testid={`maternal-edit-${m.id}`}
                      title="Edit visit"
                      className="p-1 rounded-full hover:bg-[#F1F5F9] text-[#1E3A8A]"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                {m.urgent_follow_up && <span className="text-[10px] bg-[#FEE2E2] text-[#991B1B] px-2 py-0.5 rounded-full uppercase tracking-wider">Urgent follow-up</span>}
                {m.kit_distributed && <span className="text-[10px] bg-[#E8ECE7] text-[#1E3A8A] px-2 py-0.5 rounded-full uppercase tracking-wider">Kit distributed</span>}
                {m.follow_up_date && <span className="text-[10px] bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 rounded-full">Follow-up {formatDate(m.follow_up_date)}</span>}
              </div>
              {m.wellness_concerns && <p className="text-xs text-[#334155] mt-1 whitespace-pre-wrap">{m.wellness_concerns}</p>}
              {m.notes && <p className="text-xs text-[#334155] mt-1 whitespace-pre-wrap italic">{m.notes}</p>}
            </li>
          ))}
        </ul>
      )}
      {showAdd && (
        <MaternalDialog
          csId={csId}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); onChange && onChange(); }}
        />
      )}
      {editing && (
        <MaternalDialog
          csId={csId}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange && onChange(); }}
        />
      )}
    </div>
  );
}

function MaternalDialog({ csId, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [visitType, setVisitType] = useState(existing?.visit_type || "check_in");
  const [visitDate, setVisitDate] = useState(existing?.visit_date || new Date().toISOString().slice(0, 10));
  const [followUpDate, setFollowUpDate] = useState(existing?.follow_up_date || "");
  const [kitDistributed, setKitDistributed] = useState(!!existing?.kit_distributed);
  const [urgentFollowUp, setUrgentFollowUp] = useState(!!existing?.urgent_follow_up);
  const [wellnessConcerns, setWellnessConcerns] = useState(existing?.wellness_concerns || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        visit_type: visitType,
        visit_date: visitDate,
        follow_up_date: followUpDate || null,
        kit_distributed: kitDistributed,
        urgent_follow_up: urgentFollowUp,
        wellness_concerns: wellnessConcerns.trim() || null,
        notes: notes.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/maternal/${existing.id}`, payload);
        toast.success("Visit updated");
      } else {
        await api.post(`/maternal`, { ...payload, care_seeker_id: csId });
        toast.success("Maternal visit logged");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid={isEdit ? "maternal-edit-dialog" : "maternal-add-dialog"}>
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">
            {isEdit ? "Edit maternal & family visit" : "Log maternal & family visit"}
          </h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F9F8F6]" data-testid="maternal-dialog-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Visit type</label>
            <select
              value={visitType}
              onChange={(e) => setVisitType(e.target.value)}
              data-testid="maternal-visit-type"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            >
              <option value="check_in">Check-in</option>
              <option value="postpartum">Postpartum</option>
              <option value="home_visit">Home visit</option>
              <option value="kit_distribution">Kit distribution</option>
              <option value="family_followup">Family follow-up</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">Visit date</label>
              <input
                type="date"
                value={visitDate}
                onChange={(e) => setVisitDate(e.target.value)}
                data-testid="maternal-visit-date"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">Follow-up date (optional)</label>
              <input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                data-testid="maternal-followup-date"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-[#334155]">
              <input type="checkbox" checked={kitDistributed} onChange={(e) => setKitDistributed(e.target.checked)} data-testid="maternal-kit-distributed" />
              Kit distributed
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-[#334155]">
              <input type="checkbox" checked={urgentFollowUp} onChange={(e) => setUrgentFollowUp(e.target.checked)} data-testid="maternal-urgent" />
              Urgent follow-up needed
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Wellness concerns (optional)</label>
            <textarea
              value={wellnessConcerns}
              onChange={(e) => setWellnessConcerns(e.target.value)}
              rows={2}
              data-testid="maternal-concerns"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              data-testid="maternal-notes"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} disabled={submitting} data-testid="maternal-dialog-cancel" className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155]">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="maternal-dialog-confirm" className="px-5 py-2 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40">
            {submitting ? "Saving…" : (isEdit ? "Save changes" : "Save visit")}
          </button>
        </div>
      </div>
    </div>
  );
}


const InfoRow = ({ label, value, mono }) => (
  <div className="flex justify-between items-start gap-3 text-sm">
    <span className="text-[#1E3A8A] capitalize">{label}</span>
    <span className={`text-[#0F172A] text-right font-medium ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
  </div>
);

/* ============================================================ */
/* Household chips — bidirectional Care Seeker → Care Seeker     */
/* links surfaced on each profile hero. Lets staff jump to a     */
/* partner / mother / child / sibling profile in one click       */
/* during maternal visits and household-level case work.         */
/* ============================================================ */

function HouseholdChips({ csId, csLabel }) {
  const { can } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const canEdit = !!can("care_seekers.edit");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/care-seekers/${csId}/household`);
      setMembers(data || []);
    } catch (e) {
      // Soft-fail; the section just stays empty.
      console.warn("Household load failed", e);
    } finally {
      setLoading(false);
    }
  }, [csId]);

  useEffect(() => { load(); }, [load]);

  const unlink = async (otherId, otherName) => {
    if (!window.confirm(`Unlink ${otherName} from ${csLabel}? Both profiles will lose the link.`)) return;
    try {
      await api.delete(`/care-seekers/${csId}/household/${otherId}`);
      toast.success("Unlinked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return null;
  // Hide entirely when there are no chips AND the user can't add — keeps
  // the hero clean for read-only viewers.
  if (members.length === 0 && !canEdit) return null;

  return (
    <div className="mt-6 p-4 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]" data-testid="household-chips-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Users2 className="w-4 h-4 text-[#1E3A8A]" />
          <span className="hw-eyebrow text-[10px]">Linked household members</span>
          {members.length > 0 && (
            <span className="text-[10px] text-[#334155] bg-white border border-[#E5E5E0] px-1.5 py-0.5 rounded-full">
              {members.length}
            </span>
          )}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            data-testid="household-add-btn"
            className="px-3 py-1 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#E8ECE7]"
          >
            <UserPlus className="w-3.5 h-3.5" /> Link member
          </button>
        )}
      </div>

      {members.length === 0 ? (
        <p className="text-xs text-[#1E3A8A]">
          No household members linked yet. Use Link member to connect a partner, parent, child, or sibling.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="household-chips-list">
          {members.map((m) => (
            <div
              key={m.care_seeker.id}
              data-testid={`household-chip-${m.care_seeker.id}`}
              className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] hover:border-[#1E3A8A]/40 transition-colors"
            >
              <Link
                to={`/care-seekers/${m.care_seeker.id}`}
                className="inline-flex items-center gap-1.5 text-sm text-[#0F172A] hover:text-[#1E3A8A]"
              >
                <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A] bg-[#E8ECE7] px-1.5 py-0.5 rounded-full">
                  {m.relationship}
                </span>
                <span className="font-medium">{m.care_seeker.name}</span>
                {m.care_seeker.display_id && (
                  <span className="font-mono text-[10px] text-[#1E3A8A]">{m.care_seeker.display_id}</span>
                )}
              </Link>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => unlink(m.care_seeker.id, m.care_seeker.name)}
                  data-testid={`household-unlink-${m.care_seeker.id}`}
                  title="Remove link"
                  className="p-0.5 rounded-full text-[#94A3B8] hover:bg-[#FEE2E2] hover:text-[#991B1B] opacity-60 group-hover:opacity-100 transition"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <HouseholdAddDialog
          csId={csId}
          csLabel={csLabel}
          existingIds={members.map((m) => m.care_seeker.id)}
          onClose={() => setShowAdd(false)}
          onLinked={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

const RELATIONSHIP_PRESETS = [
  "spouse", "partner", "mother", "father", "child", "sibling",
  "guardian", "grandparent", "roommate", "caregiver", "other",
];

function HouseholdAddDialog({ csId, csLabel, existingIds, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pick, setPick] = useState(null);
  const [relationship, setRelationship] = useState("partner");
  const [partnerRelationship, setPartnerRelationship] = useState("partner");
  const [submitting, setSubmitting] = useState(false);

  // Debounce-search the care seekers directory. Exclude self + already-linked.
  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/care-seekers", { params: { q: q.trim(), limit: 12 } });
        const exclude = new Set([csId, ...existingIds]);
        setResults((data || []).filter((r) => !exclude.has(r.id)).slice(0, 8));
      } catch (e) {
        console.warn("Care Seeker search failed", e);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, csId, existingIds]);

  const submit = async () => {
    if (!pick) {
      toast.error("Pick a Care Seeker to link");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/care-seekers/${csId}/household`, {
        linked_care_seeker_id: pick.id,
        relationship: relationship.trim().toLowerCase() || "household member",
        partner_relationship: partnerRelationship.trim().toLowerCase() || relationship.trim().toLowerCase() || "household member",
      });
      toast.success(`Linked ${pick.preferred_name || pick.first_name} ${pick.last_name}`);
      onLinked();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="household-add-dialog">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">Link a household member</h3>
            <p className="text-xs text-[#334155] mt-0.5">
              For <strong className="text-[#0F172A]">{csLabel}</strong>
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F9F8F6]" data-testid="household-add-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Find Care Seeker</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1E3A8A]" />
              <input
                type="search"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPick(null); }}
                placeholder="Search by name, display ID, or phone"
                data-testid="household-search-input"
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
              />
            </div>
            {pick ? (
              <div className="mt-2 px-3 py-2 rounded-lg bg-[#E8ECE7] border border-[#7E937D] flex items-center justify-between gap-2">
                <div className="text-xs">
                  <div className="font-medium text-[#0F172A]">{pick.preferred_name || pick.first_name} {pick.last_name}</div>
                  <div className="font-mono text-[10px] text-[#1E3A8A]">{pick.display_id}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setPick(null)}
                  className="text-[10px] text-[#1E3A8A] hover:underline"
                  data-testid="household-clear-pick"
                >
                  Change
                </button>
              </div>
            ) : searching ? (
              <div className="mt-2 text-xs text-[#1E3A8A]">Searching…</div>
            ) : results.length > 0 ? (
              <ul className="mt-2 border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0] max-h-60 overflow-y-auto" data-testid="household-search-results">
                {results.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setPick(r)}
                      data-testid={`household-search-result-${r.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-[#F9F8F6] flex items-center justify-between gap-2"
                    >
                      <span className="text-xs">
                        <span className="font-medium text-[#0F172A]">{r.preferred_name || r.first_name} {r.last_name}</span>
                      </span>
                      <span className="font-mono text-[10px] text-[#1E3A8A]">{r.display_id}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : q.trim().length >= 2 ? (
              <div className="mt-2 text-xs text-[#1E3A8A]">No matches.</div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">Their relationship to {csLabel.split(" ")[0]}</label>
              <input
                type="text"
                list="hh-rel-presets"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                data-testid="household-relationship"
                placeholder="e.g. spouse"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5">{csLabel.split(" ")[0]}&apos;s relationship to them</label>
              <input
                type="text"
                list="hh-rel-presets"
                value={partnerRelationship}
                onChange={(e) => setPartnerRelationship(e.target.value)}
                data-testid="household-partner-relationship"
                placeholder="e.g. spouse"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
              />
            </div>
          </div>
          <datalist id="hh-rel-presets">
            {RELATIONSHIP_PRESETS.map((r) => <option key={r} value={r} />)}
          </datalist>
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} disabled={submitting} data-testid="household-add-cancel" className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155]">Cancel</button>
          <button onClick={submit} disabled={submitting || !pick} data-testid="household-add-confirm" className="px-5 py-2 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40">
            {submitting ? "Linking…" : "Link member"}
          </button>
        </div>
      </div>
    </div>
  );
}
